import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Input, InputNumber, Row, Space, Tag, Typography, message, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LeftOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchSqlData, insertData, updateData, deleteData } from '../../../services/api';
import { MatClntIssueRequest, MatClntIssueRequestItem, VwMatClntWhInventory } from '../../../types';
import { useGetMatClntWhInventory, useGetMatClntWarehouses } from '../../../hooks/genericHooks';
import StandardTable from '../../../components/common/StandardTable';
import { useAuth } from '../../../components/common/security/AuthContext';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

interface EditableItem extends MatClntIssueRequestItem {
  key: string;
  _status?: 'unchanged' | 'updated' | 'new' | 'deleted';
  description?: string | null;
}

export default function MatClntIssueRequestEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const passedRefNo = (location.state as any)?.ref_no as string | undefined;
  const { user: username } = useAuth();

  const [header, setHeader] = useState<MatClntIssueRequest | null>(null);
  const [refNo, setRefNo] = useState<string | null>(passedRefNo || null);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [status, setStatus] = useState<string>('');
  const currentStatus = (status || header?.status || '');
  const hasItemChanges = useMemo(() => items.some(it => it._status && it._status !== 'unchanged' && it._status !== undefined), [items]);
  const hasHeaderChanges = useMemo(() => !!header && (((header.notes || '') !== (notes || '')) || ((header.status || '') !== (status || ''))), [header, notes, status]);
  const canEditItems = (currentStatus === 'pending');
  const canSave = hasItemChanges || hasHeaderChanges;
  const terminalStatuses = new Set(['issued', 'cancelled']);
  const canChangeStatus = !terminalStatuses.has((header?.status || '').toLowerCase());

  const allowedNextStatuses = useMemo(() => {
    const cur = (currentStatus || '').toLowerCase();
    switch (cur) {
      case 'pending':
        return ['approved', 'rejected', 'cancelled'];
      case 'approved':
        return ['cancelled'];
      case 'partially_issued':
        return [];
      case 'rejected':
        return ['pending', 'cancelled'];
      case 'issued':
      case 'cancelled':
        return [];
      default:
        return ['pending', 'approved', 'partially_issued', 'issued', 'rejected', 'cancelled'];
    }
  }, [currentStatus]);

  // inventory for enriching items
  const { data: invData = [], isLoading: invLoading } = useGetMatClntWhInventory() as any;
  const inventory: VwMatClntWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const invByItemcode = useMemo(() => {
    const m = new Map<string, VwMatClntWhInventory>();
    (inventory || []).forEach(r => { if (!m.has(r.itemcode)) m.set(r.itemcode, r); });
    return m;
  }, [inventory]);

  // warehouses map for displaying warehouse name and owners
  const { data: whData = [] } = useGetMatClntWarehouses() as any;
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    const list = Array.isArray(whData) ? whData : (whData?.data ?? []);
    (list || []).forEach((w: any) => { if (w?.id != null) m.set(w.id, w.name); });
    return m;
  }, [whData]);
  const whOwnersMap = useMemo(() => {
    const m = new Map<number, string[]>();
    const list = Array.isArray(whData) ? whData : (whData?.data ?? []);
    (list || []).forEach((w: any) => {
      if (w?.id != null) m.set(w.id, Array.isArray(w?.owners) ? w.owners : []);
    });
    return m;
  }, [whData]);

  // Load header by id (if needed) and then items by ref_no
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        let effectiveRef = refNo;
        if (!effectiveRef) {
          const hdrResp = await fetchSqlData('mat_clnt_issue_requests', 'id', Number(id));
          const rows: MatClntIssueRequest[] = Array.isArray(hdrResp) ? hdrResp : (hdrResp?.data ?? []);
          const hdr = rows?.[0] || null;
          if (!hdr) { message.error('Request header not found'); return; }
          setHeader(hdr);
          setRefNo(hdr.ref_no);
          setNotes(hdr.notes || '');
          setStatus(hdr.status || '');
          effectiveRef = hdr.ref_no;
        } else {
          if (!header) {
            const hdrResp = await fetchSqlData('mat_clnt_issue_requests', 'id', Number(id));
            const rows: MatClntIssueRequest[] = Array.isArray(hdrResp) ? hdrResp : (hdrResp?.data ?? []);
            const hdr = rows?.[0] || null;
            if (hdr) { setHeader(hdr); setNotes(hdr.notes || ''); setStatus(hdr.status || ''); }
          }
        }
        if (effectiveRef) {
          const itResp = await fetchSqlData('mat_clnt_issue_request_items', 'ref_no', effectiveRef);
          const itRows: MatClntIssueRequestItem[] = Array.isArray(itResp) ? itResp : (itResp?.data ?? []);
          const withKeys: EditableItem[] = (itRows || []).map(r => ({ ...r, key: String(r.id), _status: 'unchanged' }));
          setItems(withKeys);
        }
      } catch (err) {
        console.error(err);
        message.error('Failed to load request');
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const statusTag = (st?: string) => {
    const map: Record<string, { color: string; label: string }> = {
      pending: { color: 'default', label: 'Pending' },
      approved: { color: 'blue', label: 'Approved' },
      partially_issued: { color: 'gold', label: 'Partially Issued' },
      issued: { color: 'green', label: 'Issued' },
      rejected: { color: 'red', label: 'Rejected' },
      cancelled: { color: 'volcano', label: 'Cancelled' },
    };
    const cfg = (st && map[st]) || { color: 'default', label: st || '-' };
    return <Tag color={cfg.color}>{cfg.label}</Tag>;
  };

  const setQty = (rowKey: string, qty: number) => {
    setItems(prev => prev.map(it => it.key === rowKey ? { ...it, requested_qty: qty, _status: it._status === 'new' ? 'new' : 'updated' } : it));
  };

  const removeItem = (rowKey: string) => {
    setItems(prev => prev.map(it => it.key === rowKey ? { ...it, _status: 'deleted' } : it));
  };

  const visibleItems = useMemo(() => items.filter(it => it._status !== 'deleted'), [items]);

  const canApproveReject = useMemo(() => {
    const whIds = new Set<number>();
    visibleItems.forEach(it => { if (it.warehouse_id != null) whIds.add(it.warehouse_id); });
    const widArr = Array.from(whIds);
    if (widArr.length === 0) return false;
    if (!username) return false;
    return widArr.every(wid => {
      const owners = whOwnersMap.get(wid) || [];
      return owners.includes(username);
    });
  }, [visibleItems, whOwnersMap, username]);

  const filteredNextStatuses = useMemo(() => {
    if (canApproveReject) return allowedNextStatuses;
    return allowedNextStatuses.filter(s => s !== 'approved' && s !== 'rejected');
  }, [allowedNextStatuses, canApproveReject]);

  const columns: ColumnsType<EditableItem> = [
    { title: 'Itemcode', dataIndex: 'itemcode', key: 'itemcode', width: 140 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true, render: (_, row) => {
      const info = invByItemcode.get(row.itemcode);
      const desc = info?.description || row.description || row.notes || '';
      return desc;
    } },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 120, render: (v, row) => (row.size_text ?? (invByItemcode.get(row.itemcode) as any)?.size_text ?? '-') },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Requested Qty', dataIndex: 'requested_qty', key: 'requested_qty', width: 160, render: (_, row) => (
      <InputNumber min={0} disabled={!canEditItems} value={row.requested_qty} onChange={(v) => setQty(row.key, typeof v === 'number' ? v : 0)} style={{ width: 140 }} />
    ) },
    { title: 'Warehouse', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 200, render: (v) => whMap.get(v as number) || v },
    { title: 'Actions', key: 'actions', width: 100, render: (_, row) => (
      <Button action="delete" danger size="small" icon={<DeleteOutlined />} disabled={!canEditItems} onClick={() => removeItem(row.key)}>Delete</Button>
    )},
  ];

  const saveChanges = async () => {
    try {
      if (!refNo || !header) { message.warning('Header not loaded'); return; }
      setLoading(true);
      const updatePayload: any = {};
      if ((header.notes || '') !== (notes || '')) updatePayload.notes = notes;
      if ((header.status || '') !== (status || '')) {
        const next = (status || '').toLowerCase();
        if ((next === 'approved' || next === 'rejected') && !canApproveReject) {
          message.error('Only warehouse owners can approve or reject this request.');
          setLoading(false);
          return;
        }
        updatePayload.status = status;
      }
      if (Object.keys(updatePayload).length > 0) {
        await updateData('mat_clnt_issue_requests', header.id, updatePayload);
      }
      const deletions = items.filter(it => it._status === 'deleted' && it.id);
      for (const d of deletions) {
        await deleteData('mat_clnt_issue_request_items', 'id', d.id);
      }
      const updates = items.filter(it => it._status === 'updated' && it.id);
      for (const u of updates) {
        await updateData('mat_clnt_issue_request_items', u.id, { requested_qty: u.requested_qty, notes: u.notes ?? null, warehouse_id: u.warehouse_id ?? null });
      }
      const news = items.filter(it => it._status === 'new');
      if (news.length > 0) {
        const payload = news.map(n => ({
          ref_no: refNo,
          itemcode: n.itemcode,
          unit: n.unit,
          requested_qty: n.requested_qty,
          warehouse_id: n.warehouse_id ?? null,
          size_text: (invByItemcode.get(n.itemcode) as any)?.size_text ?? null,
          notes: n.notes ?? null,
        }));
        await insertData('mat_clnt_issue_request_items', payload);
      }
      message.success('Changes saved');
      try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('mat_clnt_issue_requests:changed'));
          window.dispatchEvent(new CustomEvent('mat_clnt_issue_request_items:changed'));
        }
      } catch {}
      navigate(-1);
    } catch (err) {
      console.error(err);
      message.error('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }} loading={loading || invLoading}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Edit Client Issue Request</Title>
              <Text type="secondary">Modify items and quantities, change status</Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button action="edit" type="primary" icon={<SaveOutlined />} onClick={saveChanges} disabled={!canSave}>Save Changes</Button>
          </Space>
        </Col>
      </Row>

      {/* Header info */}
      <Row style={{ marginBottom: 8 }}>
        <Col span={24}>
          <Space size={12} wrap>
            <Text><b>Ref:</b> {refNo || '-'}</Text>
            <Text><b>Date:</b> {header?.req_date ? dayjs(header.req_date).format('YYYY-MM-DD') : '-'}</Text>
            <Space>
              <Text><b>Status:</b></Text>
              <Select
                value={currentStatus || undefined}
                onChange={(v) => setStatus(v || '')}
                style={{ width: 200 }}
                disabled={!canChangeStatus}
                options={(() => {
                  const opts = new Set<string>(allowedNextStatuses);
                  if (currentStatus) opts.add(currentStatus);
                  return Array.from(opts).map(v => ({ value: v, label: v }));
                })()}
              />
              {status ? statusTag(status) : null}
            </Space>
            <Text><b>Requester:</b> {header?.requester} - {header?.requester_name}</Text>
          </Space>
        </Col>
      </Row>
      <Row style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" disabled={!canEditItems} />
        </Col>
      </Row>

      {/* Items table */}
      <StandardTable
        columns={columns as any}
        dataSource={visibleItems}
        rowKey={(r: any) => r.key}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
